/**
 * Local-first RSVP store.
 * Tries the backend when available; falls back to localStorage so the UI ships ahead of API.
 */
import type {
  PromoterGuest,
  PublicRsvpMeta,
  RsvpGuest,
  RsvpGuestCreate,
  RsvpGuestUpdate,
  RsvpSettings,
  RsvpSettingsPatch,
  TierCountMap,
} from "../types/rsvp";
import {
  DEFAULT_RSVP_CATEGORY_PRICES,
  DEFAULT_TICKET_TEMPLATE_CONFIG,
  normalizeGuestNumber,
  sumTierCounts,
} from "../types/rsvp";
import { toE164 } from "./phone";
import {
  getCompanyDefaultTemplateIdLocal,
  lineupFromArtist,
  listEventPromotersLocal,
  mergeTemplateConfig,
  resolvePromoterName,
  withResolvedTemplateSettings,
} from "./ticketTemplateStore";

const GUESTS_KEY = "zoniq.rsvp.guests.v1";
const SETTINGS_KEY = "zoniq.rsvp.settings.v1";
const EVENT_META_KEY = "zoniq.rsvp.eventMeta.v1";

type GuestBag = Record<string, RsvpGuest[]>;
type SettingsBag = Record<string, RsvpSettings>;
type EventMetaBag = Record<
  string,
  {
    name: string;
    event_date: string;
    city: string;
    artist?: string | null;
    ticket_tiers?: { id: string; name: string; price: number }[];
  }
>;

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
}

function newId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `rsvp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function newToken(): string {
  const bytes = new Uint8Array(12);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    bytes.forEach((_, i) => {
      bytes[i] = Math.floor(Math.random() * 256);
    });
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Parse IST wall time (YYYY-MM-DDTHH:mm) to UTC ISO string. */
export function parseIstWallToUtcIso(value: string): string {
  const raw = value.trim().replace(" ", "T").slice(0, 16);
  const d = new Date(`${raw}:00+05:30`);
  if (Number.isNaN(d.getTime())) throw new Error("Invalid IST datetime.");
  return d.toISOString();
}

/** Format a UTC ISO / Date as IST wall time for datetime-local. */
export function formatUtcToIstWall(value: string | Date | null | undefined): string {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

function isPastDisableAt(disableAt: string | null | undefined): boolean {
  if (!disableAt) return false;
  const t = new Date(disableAt).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() >= t;
}

function linkKindForToken(settings: RsvpSettings, token: string): "rsvp" | "guest_list" | "walk_in" | null {
  if (settings.invite_token === token) return "rsvp";
  if (settings.guest_list_token === token) return "guest_list";
  if (settings.walk_in_token === token) return "walk_in";
  return null;
}

function assertLinkOpenLocal(settings: RsvpSettings, kind: "rsvp" | "guest_list" | "walk_in"): void {
  if (kind === "walk_in") return;
  const disableAt = kind === "rsvp" ? settings.rsvp_disable_at : settings.guest_list_disable_at;
  const label = kind === "rsvp" ? "RSVP" : "Guest List";
  if (isPastDisableAt(disableAt)) {
    throw new Error(`This ${label} link is invalid or has expired.`);
  }
}

function resolvePartySize(payload: { party_size?: number; tier_counts?: TierCountMap }): number {
  const fromTiers = sumTierCounts(payload.tier_counts);
  if (fromTiers > 0) return fromTiers;
  const n = Number(payload.party_size);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
}

function defaultApproved(payload: RsvpGuestCreate): boolean {
  const channel = payload.channel ?? null;
  const source = payload.source ?? "manual";
  if (channel === "guest_list" || source === "guest_list") {
    return source === "manual";
  }
  return true;
}

function normalizeGuest(raw: RsvpGuest): RsvpGuest {
  const tier_counts = raw.tier_counts ?? {};
  const party_size =
    sumTierCounts(tier_counts) > 0 ? sumTierCounts(tier_counts) : Math.max(1, Number(raw.party_size) || 1);
  const amount =
    raw.amount != null && Number.isFinite(raw.amount)
      ? raw.amount
      : raw.amount_paid != null && Number.isFinite(raw.amount_paid)
        ? raw.amount_paid
        : null;
  const channel = raw.channel ?? null;
  const source = raw.source ?? "rsvp";
  const approved =
    raw.approved !== undefined
      ? raw.approved
      : channel === "guest_list" || source === "guest_list"
        ? source === "manual"
        : true;
  return {
    ...raw,
    tier_counts,
    party_size,
    amount,
    amount_paid: raw.amount_paid ?? amount,
    promoter: raw.promoter ?? null,
    promoter_id: raw.promoter_id ?? null,
    approved,
    source,
    channel,
    whatsapp_opt_in: raw.whatsapp_opt_in ?? false,
  };
}

export function listRsvpGuestsLocal(eventId: string): RsvpGuest[] {
  const bag = readJson<GuestBag>(GUESTS_KEY, {});
  return [...(bag[eventId] ?? [])].map(normalizeGuest).sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

export function getRsvpSettingsLocal(eventId: string): RsvpSettings {
  const bag = readJson<SettingsBag>(SETTINGS_KEY, {});
  const existing = bag[eventId];
  if (existing) {
    const migrated: RsvpSettings = {
      ...existing,
      guest_list_token: existing.guest_list_token || newToken(),
      walk_in_token: existing.walk_in_token || newToken(),
      rsvp_disable_at: existing.rsvp_disable_at ?? null,
      guest_list_disable_at: existing.guest_list_disable_at ?? null,
      rsvp_disable_at_ist:
        existing.rsvp_disable_at_ist ?? (formatUtcToIstWall(existing.rsvp_disable_at) || null),
      guest_list_disable_at_ist:
        existing.guest_list_disable_at_ist ??
        (formatUtcToIstWall(existing.guest_list_disable_at) || null),
    };
    if (
      !existing.guest_list_token ||
      !existing.walk_in_token ||
      existing.rsvp_disable_at_ist === undefined ||
      existing.guest_list_disable_at_ist === undefined
    ) {
      bag[eventId] = migrated;
      writeJson(SETTINGS_KEY, bag);
    }
    return withResolvedTemplateSettings({
      ...migrated,
      template_config: mergeTemplateConfig(migrated.template_config),
    });
  }
  const created: RsvpSettings = {
    event_id: eventId,
    invite_token: newToken(),
    guest_list_token: newToken(),
    walk_in_token: newToken(),
    category_prices: { ...DEFAULT_RSVP_CATEGORY_PRICES },
    rsvp_disable_at: null,
    guest_list_disable_at: null,
    rsvp_disable_at_ist: null,
    guest_list_disable_at_ist: null,
    ticket_template_id: null,
    template_config: { ...DEFAULT_TICKET_TEMPLATE_CONFIG },
  };
  bag[eventId] = created;
  writeJson(SETTINGS_KEY, bag);
  return withResolvedTemplateSettings(created);
}

export function updateRsvpSettingsLocal(
  eventId: string,
  patch: Partial<RsvpSettings> | RsvpSettingsPatch,
): RsvpSettings {
  const bag = readJson<SettingsBag>(SETTINGS_KEY, {});
  const current = getRsvpSettingsLocal(eventId);
  const templatePatch = patch.template_config;
  const next: RsvpSettings = {
    ...current,
    ...patch,
    event_id: eventId,
    template_config: templatePatch
      ? mergeTemplateConfig(current.template_config, templatePatch)
      : current.template_config,
  };

  if ("ticket_template_id" in patch && patch.ticket_template_id === null) {
    next.ticket_template_id = null;
  }

  if ("rsvp_disable_at_ist" in patch) {
    const ist = patch.rsvp_disable_at_ist;
    if (ist == null || ist === "") {
      next.rsvp_disable_at = null;
      next.rsvp_disable_at_ist = null;
    } else {
      next.rsvp_disable_at_ist = ist.trim().replace(" ", "T").slice(0, 16);
      next.rsvp_disable_at = parseIstWallToUtcIso(next.rsvp_disable_at_ist);
    }
  }

  if ("guest_list_disable_at_ist" in patch) {
    const ist = patch.guest_list_disable_at_ist;
    if (ist == null || ist === "") {
      next.guest_list_disable_at = null;
      next.guest_list_disable_at_ist = null;
    } else {
      next.guest_list_disable_at_ist = ist.trim().replace(" ", "T").slice(0, 16);
      next.guest_list_disable_at = parseIstWallToUtcIso(next.guest_list_disable_at_ist);
    }
  }

  bag[eventId] = next;
  writeJson(SETTINGS_KEY, bag);
  return withResolvedTemplateSettings(next);
}

export function rememberEventMetaLocal(
  eventId: string,
  meta: {
    name: string;
    event_date: string;
    city: string;
    artist?: string | null;
    ticket_tiers?: { id: string; name: string; price: number }[];
  },
): void {
  const bag = readJson<EventMetaBag>(EVENT_META_KEY, {});
  bag[eventId] = {
    ...bag[eventId],
    ...meta,
    ticket_tiers: meta.ticket_tiers ?? bag[eventId]?.ticket_tiers,
  };
  writeJson(EVENT_META_KEY, bag);
}

export function createRsvpGuestLocal(eventId: string, payload: RsvpGuestCreate): RsvpGuest {
  const lookup = normalizeGuestNumber(payload.phone);
  const phone = toE164(payload.phone) ?? lookup;
  if (!payload.name.trim()) throw new Error("Name is required.");
  if (!phone) throw new Error("Phone number is required.");

  const bag = readJson<GuestBag>(GUESTS_KEY, {});
  const list = bag[eventId] ?? [];
  if (list.some((g) => normalizeGuestNumber(g.phone) === lookup)) {
    throw new Error("A guest with this phone number is already registered for this event.");
  }

  const tier_counts = payload.tier_counts ?? {};
  const amount = payload.amount ?? payload.amount_paid ?? null;
  const promoterName =
    payload.promoter?.trim() ||
    resolvePromoterName(eventId, payload.promoter_id) ||
    null;
  const guest: RsvpGuest = {
    id: newId(),
    event_id: eventId,
    name: payload.name.trim(),
    phone,
    email: payload.email?.trim() || null,
    party_size: resolvePartySize(payload),
    category: payload.category ?? "stag",
    tier_counts,
    amount,
    amount_paid: payload.amount_paid ?? amount,
    payment_mode: payload.payment_mode ?? null,
    promoter: promoterName,
    promoter_id: payload.promoter_id ?? null,
    approved: payload.approved ?? defaultApproved(payload),
    source: payload.source ?? "manual",
    channel: payload.channel ?? null,
    instagram: payload.instagram?.trim() || null,
    whatsapp_opt_in: payload.whatsapp_opt_in ?? false,
    created_at: new Date().toISOString(),
    updated_at: null,
  };
  bag[eventId] = [guest, ...list];
  writeJson(GUESTS_KEY, bag);
  return guest;
}

export function updateRsvpGuestLocal(eventId: string, guestId: string, patch: RsvpGuestUpdate): RsvpGuest {
  const bag = readJson<GuestBag>(GUESTS_KEY, {});
  const list = bag[eventId] ?? [];
  const idx = list.findIndex((g) => g.id === guestId);
  if (idx < 0) throw new Error("Guest not found.");

  const nextLookup = patch.phone !== undefined ? normalizeGuestNumber(patch.phone) : normalizeGuestNumber(list[idx].phone);
  const nextPhone = patch.phone !== undefined ? (toE164(patch.phone) ?? nextLookup) : list[idx].phone;
  if (patch.phone !== undefined && !nextPhone) throw new Error("Phone number is required.");
  if (
    patch.phone !== undefined &&
    list.some((g, i) => i !== idx && normalizeGuestNumber(g.phone) === nextLookup)
  ) {
    throw new Error("A guest with this phone number is already registered for this event.");
  }

  const tier_counts = patch.tier_counts !== undefined ? patch.tier_counts : list[idx].tier_counts ?? {};
  const nextAmount =
    patch.amount !== undefined
      ? patch.amount
      : patch.amount_paid !== undefined
        ? patch.amount_paid
        : list[idx].amount ?? list[idx].amount_paid ?? null;
  const updated: RsvpGuest = {
    ...normalizeGuest(list[idx]),
    name: patch.name !== undefined ? patch.name.trim() : list[idx].name,
    phone: nextPhone,
    email: patch.email !== undefined ? patch.email?.trim() || null : list[idx].email,
    tier_counts,
    party_size: resolvePartySize({
      party_size: patch.party_size ?? list[idx].party_size,
      tier_counts,
    }),
    category: patch.category ?? list[idx].category,
    amount: nextAmount,
    amount_paid: patch.amount_paid !== undefined ? patch.amount_paid : nextAmount,
    payment_mode:
      patch.payment_mode !== undefined ? patch.payment_mode : list[idx].payment_mode ?? null,
    promoter:
      patch.promoter !== undefined
        ? patch.promoter?.trim() || null
        : patch.promoter_id !== undefined
          ? resolvePromoterName(eventId, patch.promoter_id) ?? list[idx].promoter ?? null
          : list[idx].promoter ?? null,
    promoter_id:
      patch.promoter_id !== undefined ? patch.promoter_id : list[idx].promoter_id ?? null,
    approved: patch.approved !== undefined ? patch.approved : list[idx].approved ?? true,
    source: patch.source ?? list[idx].source,
    channel: patch.channel !== undefined ? patch.channel : list[idx].channel ?? null,
    instagram:
      patch.instagram !== undefined ? patch.instagram?.trim() || null : list[idx].instagram,
    whatsapp_opt_in:
      patch.whatsapp_opt_in !== undefined ? patch.whatsapp_opt_in : list[idx].whatsapp_opt_in ?? false,
    updated_at: new Date().toISOString(),
  };
  if (!updated.name) throw new Error("Name is required.");

  list[idx] = updated;
  bag[eventId] = list;
  writeJson(GUESTS_KEY, bag);
  return updated;
}

export function deleteRsvpGuestLocal(eventId: string, guestId: string): void {
  const bag = readJson<GuestBag>(GUESTS_KEY, {});
  bag[eventId] = (bag[eventId] ?? []).filter((g) => g.id !== guestId);
  writeJson(GUESTS_KEY, bag);
}

export function findRsvpGuestByPhoneLocal(eventId: string, phone: string): RsvpGuest | null {
  const normalized = normalizeGuestNumber(phone);
  return listRsvpGuestsLocal(eventId).find((g) => normalizeGuestNumber(g.phone) === normalized) ?? null;
}

export function findSettingsByTokenLocal(token: string): RsvpSettings | null {
  const bag = readJson<SettingsBag>(SETTINGS_KEY, {});
  return (
    Object.values(bag).find(
      (s) =>
        s.invite_token === token ||
        s.guest_list_token === token ||
        s.walk_in_token === token,
    ) ?? null
  );
}

export function getPublicRsvpMetaLocal(token: string): PublicRsvpMeta | null {
  const settings = findSettingsByTokenLocal(token);
  if (!settings) return null;
  const kind = linkKindForToken(settings, token);
  if (!kind || kind === "walk_in") {
    // Walk-in uses this meta helper too; allow when kind is walk_in
  }
  if (kind === "rsvp" || kind === "guest_list") {
    try {
      assertLinkOpenLocal(settings, kind);
    } catch {
      return null;
    }
  }
  const metaBag = readJson<EventMetaBag>(EVENT_META_KEY, {});
  const meta = metaBag[settings.event_id];
  const resolved = withResolvedTemplateSettings(settings);
  const template_config = mergeTemplateConfig(resolved.template_config);
  const artist = meta?.artist ?? settings.artist ?? null;
  const promoters =
    kind === "guest_list"
      ? listEventPromotersLocal(settings.event_id).map((p) => ({ id: p.id, name: p.name }))
      : [];
  return {
    event_id: settings.event_id,
    event_name: meta?.name ?? settings.event_name ?? "Event",
    event_date: meta?.event_date ?? settings.event_date ?? "",
    city: meta?.city ?? settings.city ?? "",
    artist,
    invite_token: token,
    link_kind: kind === "guest_list" ? "guest_list" : kind === "rsvp" ? "rsvp" : undefined,
    ticket_tiers: meta?.ticket_tiers ?? [],
    ticket_template_id: resolved.resolved_ticket_template_id ?? getCompanyDefaultTemplateIdLocal(),
    template_config,
    banner_url: template_config.banner_url ?? null,
    lineup: lineupFromArtist(artist),
    promoters,
  };
}

export function submitPublicRsvpLocal(token: string, payload: RsvpGuestCreate): RsvpGuest {
  const settings = findSettingsByTokenLocal(token);
  if (!settings) throw new Error("This RSVP link is invalid or has expired.");
  const kind = linkKindForToken(settings, token);
  if (!kind) throw new Error("This RSVP link is invalid or has expired.");
  if (kind === "rsvp" || kind === "guest_list") {
    assertLinkOpenLocal(settings, kind);
  }
  const source =
    payload.source ??
    (kind === "guest_list" ? "guest_list" : kind === "walk_in" ? "walk_in" : "rsvp");
  const channel =
    kind === "rsvp" || kind === "guest_list" || kind === "walk_in" ? kind : payload.channel ?? null;
  return createRsvpGuestLocal(settings.event_id, { ...payload, source, channel });
}

export function listPromoterGuestsLocal(): PromoterGuest[] {
  const bag = readJson<GuestBag>(GUESTS_KEY, {});
  const metaBag = readJson<EventMetaBag>(EVENT_META_KEY, {});
  const byPhone = new Map<
    string,
    {
      name: string;
      instagram?: string | null;
      events: Set<string>;
      artists: Map<string, number>;
    }
  >();

  Object.entries(bag).forEach(([eventId, guests]) => {
    const meta = metaBag[eventId];
    const eventLabel = meta?.name ?? eventId;
    const artist = meta?.artist?.trim() || null;
    guests.forEach((g) => {
      const phone = normalizeGuestNumber(g.phone);
      const row = byPhone.get(phone) ?? {
        name: g.name,
        instagram: g.instagram,
        events: new Set<string>(),
        artists: new Map<string, number>(),
      };
      row.name = g.name;
      if (g.instagram) row.instagram = g.instagram;
      row.events.add(eventLabel);
      if (artist) row.artists.set(artist, (row.artists.get(artist) ?? 0) + 1);
      byPhone.set(phone, row);
    });
  });

  return Array.from(byPhone.entries())
    .map(([phone, row]) => {
      let favourite: string | null = null;
      let best = 0;
      row.artists.forEach((count, name) => {
        if (count > best) {
          best = count;
          favourite = name;
        }
      });
      return {
        phone,
        name: row.name,
        instagram: row.instagram ?? null,
        events_attended: row.events.size,
        event_names: Array.from(row.events).sort((a, b) => a.localeCompare(b)),
        favourite_artist: favourite,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function projectedRsvpRevenue(
  guests: RsvpGuest[],
  prices: Record<string, number> = DEFAULT_RSVP_CATEGORY_PRICES,
): number {
  return guests.reduce((sum, g) => {
    if (g.amount != null && Number.isFinite(g.amount)) return sum + g.amount;
    const unit = prices[g.category] ?? 0;
    return sum + unit * Math.max(1, g.party_size);
  }, 0);
}

export function rsvpHeadcount(guests: RsvpGuest[]): number {
  return guests.reduce((sum, g) => sum + Math.max(1, g.party_size), 0);
}

/** Update name/instagram for every RSVP row matching this phone. */
export function updatePromoterGuestLocal(
  phone: string,
  patch: { name: string; instagram?: string | null },
): void {
  const normalized = normalizeGuestNumber(phone);
  if (!patch.name.trim()) throw new Error("Name is required.");
  const bag = readJson<GuestBag>(GUESTS_KEY, {});
  let touched = false;
  Object.keys(bag).forEach((eventId) => {
    bag[eventId] = bag[eventId].map((g) => {
      if (normalizeGuestNumber(g.phone) !== normalized) return g;
      touched = true;
      return {
        ...g,
        name: patch.name.trim(),
        instagram: patch.instagram ?? null,
        updated_at: new Date().toISOString(),
      };
    });
  });
  if (!touched) throw new Error("Guest not found.");
  writeJson(GUESTS_KEY, bag);
}

/** Remove all RSVP rows for this phone across events. */
export function deletePromoterGuestLocal(phone: string): void {
  const normalized = normalizeGuestNumber(phone);
  const bag = readJson<GuestBag>(GUESTS_KEY, {});
  Object.keys(bag).forEach((eventId) => {
    bag[eventId] = bag[eventId].filter((g) => normalizeGuestNumber(g.phone) !== normalized);
  });
  writeJson(GUESTS_KEY, bag);
}
